import React, { useEffect, useState } from 'react';
import {
    generatePath, useLocation, useNavigate, useParams, useSearchParams,
} from 'react-router-dom';
import { DealTypeToId } from 'constants/deals';
import useAuthContext from 'contexts/AuthContext';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import useToast from 'hooks/useToast';
import useS3Upload from 'hooks/useS3Upload';
import { isEmpty } from 'lodash';
import {
    countMappingErrors,
    getMappingData, getPageSubtitle, getPageTitle, matchMappingWithDefaultStructure,
} from 'views/ProFormaUpload/helpers';
import RenderIf from 'components/RenderIf/RenderIf';
import { useQueryClient } from '@tanstack/react-query';
import { NSButton } from '../../../../bricks';
import PageTitle from '../../../../components/PageTitle/PageTitle';
import { URLS } from '../../../../services/urls';
import {
    ACCEPTED_PRO_FORMA_FILE_TYPES,
    DEVELOPMENT_DEAL_PRO_FORMA_MAPPING,
    ACQUISITION_DEAL_PRO_FORMA_MAPPING,
} from '../../constants';
import useGetTemporaryProFormaUrl from '../../../../queries/UploadedProForma/useGetTemporaryProFormaUrl';
import useCreateTemporaryProFormaUrl from '../../../../queries/UploadedProForma/useCreateTemporaryProFormaUrl';
import {
    IInvalidRows,
    IMapping, IProFormaMappingRow, ITemporaryUploadedFile, IUploadedFile,
} from '../../types';
import {
    useCreateUploadedProForma, useGetProFormaMapping, useGetUploadedProForma, useUpdateUploadedProForma,
} from '../../../../queries/UploadedProForma';
import useCreateProFormaMapping from '../../../../queries/UploadedProForma/useCreateProFormaMapping';
import FileContainer from './FileContainer/FileContainer';
import UploadedProFormaViewOnly from './UploadedProFormaViewOnly/UploadedProFormaViewOnly';
import { MappingTables } from '../MappingTables/MappingTables';

const ProFormaUpload = () => {
    const navigate = useNavigate();
    const { selectedOrganizationId } = useAuthContext();
    const { dealId, proFormaId } = useParams<{ dealId: string; proFormaId?: string }>();

    const [searchParams] = useSearchParams();
    const shouldEdit = searchParams.get('shouldEdit') === 'true';

    const proFormaHomePath = generatePath(URLS.PRO_FORMA.HOME, { dealId });
    const { showSuccess, showError } = useToast();
    const queryClient = useQueryClient();
    const location = useLocation();

    const [uploadedFile, setUploadedFile] = useState<IUploadedFile | null>(null);
    const [temporaryProFormaFile, setTemporaryProFormaFile] = useState<ITemporaryUploadedFile | null>(null);
    const [mapping, setMapping] = useState<IMapping | null>(null);
    const [useMappingForFutureDeals, setUseMappingForFutureDeals] = useState(true);
    const [extractedSheetNames, setExtractedSheetNames] = useState<string[]>([]);
    const [mappingIssuesCount, setMappingIssuesCount] = useState(0);
    const [isEditMode, setIsEditMode] = useState(false);
    const [pageTitle, setPageTitle] = useState<string>('Upload pro forma');
    const [pageSubtitle, setPageSubtitle] = useState<string>('Use an existing .xls, .xlsx, or .xlsm file to populate a new pro forma.');
    const [createdFileId, setCreatedFileId] = useState<number | null>(null);
    const [invalidRows, setInvalidRows] = useState<IInvalidRows>({});
    const [showMappingErrors, setShowMappingErrors] = useState(false);

    const { data: fetchedDeal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });
    const dealTypeId = fetchedDeal?.dealType?.id || 0;
    const dealTypeName = fetchedDeal?.dealType?.name || '';

    const { data: proFormaMapping } = useGetProFormaMapping(selectedOrganizationId!, fetchedDeal?.dealType?.id || 0, {
        enabled: Boolean(selectedOrganizationId) && Boolean(fetchedDeal?.dealType?.id),
    });

    const { data: uploadedProForma } = useGetUploadedProForma(selectedOrganizationId!, Number(proFormaId), {
        enabled: !!Number(proFormaId) && !!selectedOrganizationId,
    });

    // creating s3 url in a temporary bucket
    const { mutateAsync: createPresignedProFormaUrl } = useCreateTemporaryProFormaUrl();

    // dropping the file
    const handleFileUpload = async (
        event: React.DragEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>,
    ): Promise<File[]> => {
        if (uploadedFile) {
            return [];
        }

        let fileList: FileList | null = null;

        if ('dataTransfer' in event && event.dataTransfer) {
            fileList = event.dataTransfer.files;
        } else if (event.target && (event.target as HTMLInputElement).files) {
            fileList = (event.target as HTMLInputElement).files;
        }

        const filesArray = fileList ? Array.from(fileList) : [];

        if (filesArray.length > 1) {
            showError('Please upload only one file.');
            return [];
        }

        const file = filesArray[0];

        if (file) {
            if (!ACCEPTED_PRO_FORMA_FILE_TYPES.includes(file.type)) {
                showError('Please upload Microsoft Excel file types only');
                return [];
            }

            setUploadedFile({ id: 1, file });

            createPresignedProFormaUrl({
                organizationUuid: selectedOrganizationId!,
                dealId: Number(dealId),
                postData: { fileName: file.name },
            }, {
                onSuccess: res => {
                    uploadToS3Bucket({
                        urlData: res,
                        file,
                        id: res.id,
                    });
                },
                onError: () => showError('Failed to upload pro forma.'),
            });
        }

        return filesArray;
    };

    // uploading file to s3 bucket
    const { mutateAsync: uploadToS3Bucket } = useS3Upload({
        onSuccess: (res: any) => {
            const url = res.config.url!;
            const bucketName = url.split('//')[1].split('.')[0];
            const objectKey = res.config.data.get('key');
            const fileName = res?.config?.data.get('file').name;
            if (uploadedFile?.id) {
                setTemporaryProFormaFile({
                    id: uploadedFile?.id,
                    name: fileName,
                    objectKey,
                    bucketName,
                });
                showSuccess('Pro forma uploaded successfully.');
            }
        },
        onError: error => console.error('Failed to save uploaded pro forma.', error),
    });

    // getting s3 preview url and sheet names
    const { data: previewFileData } = useGetTemporaryProFormaUrl(selectedOrganizationId!, Number(dealId), temporaryProFormaFile?.objectKey || '', {
        enabled: Boolean(selectedOrganizationId) && Boolean(temporaryProFormaFile?.objectKey),
    });

    const { mutate: createProFormaFile, isPending: isPendingProFormaFileCreation } = useCreateUploadedProForma();
    const { mutate: createMapping, isPending: isPendingMapping } = useCreateProFormaMapping();
    const { mutate: updateUploadedProForma, isPending: isPendingProFormaUpdate } = useUpdateUploadedProForma();

    // updating template mapping if applicable and pro forma mapping
    const handleUpdateProFormaMapping = (uploadedProFormaId?: number) => {
        const mappedData = getMappingData(mapping!, dealTypeId);
        const cleanedData = {
            ...mappedData,
            sources: Object.values(mappedData.sources || []).filter(
                ({ name, sheet, cell }) => !(name === '' && sheet === '' && cell === ''),
            ),
        };

        if (mapping && dealTypeId) {
            // create mapping for future deals of given deal type
            if (useMappingForFutureDeals) {
                createMapping({
                    organizationUuid: selectedOrganizationId!,
                    postData: cleanedData,
                }, {
                    onSuccess: () => console.log('Pro forma mapping saved successfully.'),
                    onError: () => showError('Failed to save pro forma mapping.'),
                });
            }

            // save mapping to the uploaded file
            updateUploadedProForma({
                organizationUuid: selectedOrganizationId!,
                uploadedProFormaId: uploadedProFormaId || Number(proFormaId),
                patchData: cleanedData,
            }, {
                onSuccess: () => {
                    showSuccess('Mapping for the uploaded pro forma updated successfully.');
                    setIsEditMode(false);

                    queryClient.invalidateQueries({ queryKey: ['ProFormaMapping'] });
                    queryClient.invalidateQueries({ queryKey: ['UploadedProForma'] });

                    if (uploadedProFormaId && !proFormaId) {
                        // navigate only from create pro forma
                        navigate(`${location.pathname}/${uploadedProFormaId}`);
                    }
                },
                onError: (error: any) => {
                    showError(`Failed to update pro forma mapping. ${error.response?.data?.detail}`);
                },
            });
        }
    };

    const handleCreateProFormaFileAndUpdateMapping = () => {
        // in case first time saving mappping errored out and we've created pro forma already
        // update the mapping without trying to create pro forma again
        if (createdFileId) {
            handleUpdateProFormaMapping(createdFileId);
        } else {
            createProFormaFile({
                organizationUuid: selectedOrganizationId!,
                dealId: Number(dealId),
                postData: {
                    dealTypeId,
                    fileName: temporaryProFormaFile?.name || '',
                    fileSize: uploadedFile?.file?.size || 0,
                },
            }, {
                onSuccess: res => {
                    showSuccess('Pro forma file saved successfully.');
                    // save mapping to the uploaded file
                    handleUpdateProFormaMapping(res.id);
                    setCreatedFileId(res.id);
                },
                onError: () => showError('Failed to save pro forma file.'),
            });
        }
    };

    const countErrors = (mapping: Partial<Record<string, IProFormaMappingRow[]>> | null) => {
        if (mapping) {
            const { count, errorRows } = countMappingErrors(mapping, extractedSheetNames);
            setMappingIssuesCount(count);
            setInvalidRows(errorRows);
            return count;
        }
        return 0;
    };

    const finishCallback = () => {
        const errors = countErrors(mapping);

        if (errors === 0) {
            if (proFormaId) {
                handleUpdateProFormaMapping();
            } else {
                handleCreateProFormaFileAndUpdateMapping();
            }
            setShowMappingErrors(false);
        } else {
            setShowMappingErrors(true);
        }
    };

    const redirectToUploadedProFormasTable = () => {
        const path = generatePath(URLS.PRO_FORMA.HOME, { dealId });
        navigate(path);
    };

    const getMappingDefaultStructure = () => {
        if (fetchedDeal && fetchedDeal?.dealType && !mapping) {
            const isDevelopment = fetchedDeal.dealType.id === DealTypeToId.Development;
            const defaultMapping = isDevelopment
                ? DEVELOPMENT_DEAL_PRO_FORMA_MAPPING
                : ACQUISITION_DEAL_PRO_FORMA_MAPPING;

            setMapping(defaultMapping);
        }
    };

    useEffect(() => {
        getMappingDefaultStructure();
    }, [fetchedDeal, mapping]);

    useEffect(() => {
        if (proFormaMapping && !isEmpty(proFormaMapping) && !proFormaId) {
            const groupedMapping = matchMappingWithDefaultStructure(fetchedDeal?.dealType?.id || 0, proFormaMapping.mappingRows);
            if (groupedMapping) {
                setMapping(groupedMapping);
            }
        }
    }, [proFormaMapping]);

    useEffect(() => {
        if (uploadedProForma && !isEmpty(uploadedProForma)) {
            if (proFormaId && !!Object.keys(uploadedProForma?.excelMapping || {}).length) {
                const groupedMapping = matchMappingWithDefaultStructure(
                    uploadedProForma?.dealTypeId || 0, JSON.parse(uploadedProForma.excelMapping || '[]'),
                );
                if (groupedMapping) {
                    setMapping(groupedMapping);
                }
            } else {
                getMappingDefaultStructure();
            }
            if (uploadedProForma?.sheetNames) {
                setExtractedSheetNames(uploadedProForma?.sheetNames || []);
            }

            const title = getPageTitle(uploadedProForma, proFormaId, isEditMode);
            setPageTitle(title);
            const subtitle = getPageSubtitle(proFormaId, isEditMode);
            setPageSubtitle(subtitle);
        }
    }, [uploadedProForma, isEditMode]);

    useEffect(() => {
        if (previewFileData && previewFileData?.sheetNames) {
            setExtractedSheetNames(previewFileData?.sheetNames || []);
        }
    }, [previewFileData]);

    useEffect(() => {
        if (!proFormaId || shouldEdit) {
            setIsEditMode(true);
        }
    }, [proFormaId, shouldEdit]);

    useEffect(() => {
        if (mappingIssuesCount === 0 && showMappingErrors) {
            setShowMappingErrors(false);
        }
    }, [mappingIssuesCount, showMappingErrors]);

    return (
        <div className="h-100 d-flex flex-column">
            <PageTitle
                title={pageTitle}
                subtitle={pageSubtitle}
                breadcrumbBackLinkText="Back"
                breadcrumbBackLinkCallback={redirectToUploadedProFormasTable}
                containerClassName="mb-4"
                beta
                betaCustomText="Beta"
            >
                <RenderIf isTrue={!proFormaId || isEditMode}>
                    <NSButton
                        className="mr-2 heap-deal-pro-forma-exit-upload-mapping"
                        color="secondary"
                        text="Back"
                        callback={() => navigate(proFormaHomePath)}
                    />
                    <NSButton
                        color="primary"
                        text="Finish"
                        className="heap-deal-pro-forma-finish-upload-mapping"
                        callback={finishCallback}
                        isLoading={isPendingProFormaFileCreation || isPendingMapping || isPendingProFormaUpdate}
                        disabled={!(previewFileData || uploadedProForma)}
                    />
                </RenderIf>

                <RenderIf isTrue={proFormaId && !isEditMode}>
                    <NSButton color="primary" text="Edit mapping" callback={() => setIsEditMode(!isEditMode)} />
                </RenderIf>
            </PageTitle>

            <RenderIf isTrue={!!mapping && !isEmpty(mapping)}>
                <div className="d-flex flex-grow-1">
                    <FileContainer
                        previewUrl={!proFormaId ? previewFileData?.previewUrl || null : uploadedProForma?.s3Url || null}
                        isPreviewLoading={!!uploadedFile && !previewFileData}
                        handleFileUpload={handleFileUpload}
                    />

                    <RenderIf
                        isTrue={!isEditMode && !!mapping && proFormaId && !!uploadedProForma}
                        isLoading={isEmpty(mapping) && !uploadedProForma}
                    >
                        <UploadedProFormaViewOnly
                            uploadedProForma={uploadedProForma}
                            mapping={mapping!}
                        />
                    </RenderIf>

                    <RenderIf isTrue={isEditMode && !!dealTypeName && !!mapping && !isEmpty(mapping)}>
                        <div className="w-50 d-flex flex-column">
                            <MappingTables
                                isDisabled={!proFormaId && !previewFileData?.previewUrl}
                                dealTypeName={dealTypeName}
                                mapping={mapping!}
                                setMapping={setMapping as any}
                                useMappingForFutureDeals={useMappingForFutureDeals}
                                setUseMappingForFutureDeals={setUseMappingForFutureDeals}
                                issuesCount={mappingIssuesCount}
                                invalidRows={invalidRows}
                                showMappingErrors={showMappingErrors}
                            />
                        </div>
                    </RenderIf>
                </div>
            </RenderIf>
        </div>
    );
};

export default ProFormaUpload;
